import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Icon, PageHeader, StatusBadge } from '../../components';
import { apiRequest } from '../../lib/api';
import type { ListResponse } from '../../lib/useCrud';
import { ClientFormModal } from './ClientFormModal';
import type { ClientPayload, ClientRow } from './types';
import type { UserOption } from '../leads/types';

export function ClientDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const clientQ = useQuery({
    queryKey: ['client', id],
    queryFn: () => apiRequest<{ data: ClientRow }>(`/clients/${id}`),
    enabled: !!id,
  });
  const usersQ = useQuery({
    queryKey: ['lookup', 'users'],
    queryFn: () =>
      apiRequest<ListResponse<UserOption>>('/users?limit=200&include_inactive=false'),
  });

  const updateMut = useMutation({
    mutationFn: (body: ClientPayload) =>
      apiRequest<{ data: ClientRow }>(`/clients/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', id] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  if (clientQ.isLoading) return <div className="card card-body muted">Loading…</div>;
  if (clientQ.error) {
    return (
      <div className="alert">
        {clientQ.error instanceof Error ? clientQ.error.message : 'Failed to load'}
      </div>
    );
  }
  const c = clientQ.data?.data;
  if (!c) return null;

  const users = usersQ.data?.data ?? [];
  const sales = users.find((u) => u.id === c.assigned_sales_user_id);
  const pse = users.find((u) => u.id === c.assigned_pse_user_id);

  return (
    <>
      <PageHeader
        crumb={<Link to="/clients">Clients</Link>}
        title={c.full_name ?? 'Unnamed client'}
        subtitle={c.email ?? c.company ?? '—'}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {c.source_lead_id && (
              <Link
                to={`/leads/${c.source_lead_id}`}
                className="badge green"
                title="Open the lead this client was converted from"
                style={{ textDecoration: 'none' }}
              >
                <span className="badge-dot" />
                Converted from lead
              </Link>
            )}
            <Button icon="edit" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </div>
        }
      />
      <div className="card">
        <div
          className="card-body"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}
        >
          <Field label="Full name" value={c.full_name} />
          <Field label="Email" value={c.email} />
          <Field label="Phone" value={c.phone} />
          <Field label="Company" value={c.company} />
          <Field label="Country" value={c.country} />
          <Field label="Address" value={c.address} />
          <Field label="Date of birth" value={c.date_of_birth} />
          <Field label="Category" value={c.client_category} />
          <Field label="Assigned sales" value={sales?.full_name ?? null} />
          <Field label="Assigned PSE" value={pse?.full_name ?? null} />
          <div>
            <div className="field-label">Active</div>
            <StatusBadge active={c.is_active} />
          </div>
          {c.source_lead_id && (
            <div>
              <div className="field-label">Converted from</div>
              <Link
                to={`/leads/${c.source_lead_id}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                View source lead
                <Icon name="arrowRight" size={14} />
              </Link>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ClientFormModal
          row={c}
          users={users}
          onClose={() => setEditing(false)}
          onSubmit={async (payload) => {
            await updateMut.mutateAsync(payload);
            setEditing(false);
          }}
        />
      )}
    </>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="field-label">{label}</div>
      <div>{value || <span className="muted">—</span>}</div>
    </div>
  );
}
